import { FC } from "react";
import { Box } from "@chakra-ui/react";
import { Header } from "@/components/Header";
import { Seo } from "@/components/Seo";
import { BreadcrumbSchema, ProductSchema } from "@/components/StructuredData";
import { PublicLayout } from "@/layouts/PublicLayout";
import { LandingPageContent, SUPPORTED_LOCALES, Locale } from "@/constants/landingPages";
import { LandingHeroSection } from "./sections/LandingHeroSection";
import { LandingBenefitsSection } from "./sections/LandingBenefitsSection";
import { LandingIntegrationSection } from "./sections/LandingIntegrationSection";
import { LandingFeaturesSection } from "./sections/LandingFeaturesSection";
import { LandingPricingSection } from "./sections/LandingPricingSection";
import { LandingHowItWorksSection } from "./sections/LandingHowItWorksSection";
import { LandingCtaSection } from "./sections/LandingCtaSection";

interface LandingPageProps {
  locale: Locale;
  platform: string;
  content: LandingPageContent;
}

export const LandingPage: FC<LandingPageProps> = ({ locale, platform, content }) => {
  const baseUrl = process.env.NEXT_PUBLIC_URL || "https://flexibooking.sk";
  const currentUrl = `${baseUrl}/${locale}/${platform}`;

  // Generate hreflang tags for all supported locales
  const hreflangs = SUPPORTED_LOCALES.map((loc) => ({
    locale: loc,
    url: `${baseUrl}/${loc}/${platform}`,
  }));

  return (
    <PublicLayout>
      <Seo
        title={content.meta.title}
        description={content.meta.description}
        ogTitle={content.meta.ogTitle}
        ogDescription={content.meta.ogDescription}
        url={currentUrl}
        canonical={currentUrl}
        hreflangs={hreflangs}
      />

      {/* Structured Data for SEO */}
      <BreadcrumbSchema locale={locale} platform={platform} />
      <ProductSchema platform={platform} locale={locale} />

      <Header bg="white" isWithBreadcrumbs={false} locale={locale} content={content} />
      <Box as="main">
        <LandingHeroSection content={content.hero} locale={locale} />
        <LandingBenefitsSection content={content.benefits} />
        <LandingIntegrationSection content={content.integration} />
        <LandingFeaturesSection content={content.features} />
        <Box id="pricing">
          <LandingPricingSection content={content.pricing} />
        </Box>
        <LandingHowItWorksSection content={content.howItWorks} />
        <LandingCtaSection content={content.finalCta} locale={locale} />
      </Box>
    </PublicLayout>
  );
};
